import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, CheckCircle2, Facebook, MessageCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PageBanner } from "../components/Shared";
import { useContent } from "../context/ContentContext";
import { sendInquiry } from "../lib/api";
import { buildWaMessage, openWhatsApp } from "../lib/whatsapp";
import { usePageMeta } from "../hooks/usePageMeta";

const Contact = () => {
  const { t, lang } = useLang();
  usePageMeta({ title: "Contact Fly Jannah — Dhaka Hajj & Umrah Agency", description: "Contact Golam Rabbii at Fly Jannah Tours & Travels, Block F Road 14 House 2, South Banasree, Dhaka. Cell & WhatsApp 01713-477907, grabbi@fly-jannah.com." });
  const { CONTACT, IMAGES } = useContent();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    sendInquiry("contact", form).catch(() => {});
    openWhatsApp(CONTACT, buildWaMessage(lang === "en" ? "New Contact Message" : "নতুন যোগাযোগ বার্তা", [[t.contact.name, form.name], [t.contact.email, form.email], [t.contact.subject, form.subject], [t.contact.msg, form.message]]));
    setDone(true);
    setForm({ name: "", email: "", subject: "", message: "" });
    setTimeout(() => setDone(false), 5000);
  };

  const inputCls =
    "w-full px-4 py-3 rounded-lg border border-jannah-sand bg-white focus:outline-none focus:ring-2 focus:ring-jannah-green/40";

  const cards = [
    { icon: MapPin, label: t.contact.address, val: lang === "en" ? CONTACT.address_en : CONTACT.address_bn },
    { icon: Phone, label: t.contact.phone, val: `${CONTACT.person}\n${CONTACT.phone} (${lang === "en" ? "Cell & WhatsApp" : "মোবাইল ও হোয়াটসঅ্যাপ"})` },
    { icon: Mail, label: t.contact.email, val: CONTACT.email },
    { icon: Clock, label: t.contact.hours, val: (lang === "en" ? CONTACT.hours_en : CONTACT.hours_bn) || t.top_hours },
  ];

  return (
    <div>
      <PageBanner title={t.contact.title} subtitle={t.contact.sub} image={IMAGES.pageBanner} />

      <section className="section-pad bg-jannah-cream">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {cards.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-6 border border-jannah-sand text-center card-hover">
                  <div className="w-14 h-14 rounded-full bg-jannah-green/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-jannah-green" size={24} />
                  </div>
                  <h3 className="font-semibold text-jannah-green-dark mb-1">{c.label}</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{c.val}</p>
                </div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h2 className="text-3xl font-bold text-jannah-green-dark mb-6">{t.contact.form_title}</h2>
              {done && (
                <div className="mb-5 flex items-center gap-3 bg-jannah-green/10 text-jannah-green rounded-lg p-4">
                  <CheckCircle2 size={20} /> {t.contact.success}
                </div>
              )}
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input required placeholder={t.contact.name} value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                  <input required type="email" placeholder={t.contact.email} value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                </div>
                <input required placeholder={t.contact.subject} value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-input" />
                <textarea required rows="5" placeholder={t.contact.msg} value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" />
                <button type="submit" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full btn-gold font-semibold" data-testid="contact-submit-btn">
                  {lang === "en" ? "Send via WhatsApp" : "হোয়াটসঅ্যাপে পাঠান"} <MessageCircle size={17} />
                </button>
              </form>
              <div className="mt-6 flex items-center gap-3">
                <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-jannah-green text-white text-sm font-semibold hover:bg-jannah-green-dark">
                  <Facebook size={16} /> Facebook
                </a>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-jannah-green text-jannah-green text-sm font-semibold hover:bg-jannah-green/10">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-jannah-sand min-h-[420px] shadow-lg">
              <iframe
                title="Fly Jannah location"
                src={CONTACT.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "420px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
